/**
 * 电商复刻应用 · API 客户端（自平台 services/api.ts 拆出，随应用独立演进）
 */
import { platformFetch } from '../../platform/http';

export interface EcommerceReplicateTask {
  id: string;
  title: string;
  productImage: string | null;
  productImage2: string | null;
  productImage3: string | null;
  productDescription: string | null;
  nodeCount: number;
  completedCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface EcommerceReplicateNode {
  id: string;
  taskId: string;
  title: string;
  referenceImage: string | null;
  prompt: string | null;
  replicateStrength: 'low' | 'medium' | 'high';
  // 生成参数
  model: string | null;
  size: string;
  quality: 'low' | 'medium' | 'high';
  status: 'idle' | 'generating' | 'success' | 'failed';
  outputImage: string | null;
  errorMessage: string | null;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface EcommerceReplicateTaskDetail extends EcommerceReplicateTask {
  nodes: EcommerceReplicateNode[];
}

export interface EcommerceCreditsPreview {
  credits: number;
  nodeCount: number;
  currentCredits: number;
  enough: boolean;
  nodes?: Array<{ nodeId: string; title: string; credits: number }>;
}

export const ecommerceReplicateAPI = {
  // 获取任务列表
  async getTasks(): Promise<{ data: EcommerceReplicateTask[] }> {
    const response = await platformFetch('/ecommerce-replicate/tasks');
    if (!response.ok) throw new Error('获取任务列表失败');
    return response.json();
  },

  // 获取任务详情
  async getTask(id: string): Promise<{ data: EcommerceReplicateTaskDetail }> {
    const response = await platformFetch(`/ecommerce-replicate/tasks/${id}`);
    if (!response.ok) throw new Error('获取任务详情失败');
    return response.json();
  },

  // 创建任务
  async createTask(data: { title: string; productImage?: string; productImage2?: string; productImage3?: string; productDescription?: string }): Promise<{ data: EcommerceReplicateTaskDetail }> {
    const response = await platformFetch('/ecommerce-replicate/tasks', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '创建任务失败');
    }
    return response.json();
  },

  // 更新任务
  async updateTask(id: string, data: { title?: string; productImage?: string | null; productImage2?: string | null; productImage3?: string | null; productDescription?: string | null }): Promise<{ data: EcommerceReplicateTaskDetail }> {
    const response = await platformFetch(`/ecommerce-replicate/tasks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '更新任务失败');
    }
    return response.json();
  },

  // 删除任务
  async deleteTask(id: string): Promise<{ message: string }> {
    const response = await platformFetch(`/ecommerce-replicate/tasks/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '删除任务失败');
    }
    return response.json();
  },

  // 创建节点
  async createNode(taskId: string, data: {
    title?: string;
    referenceImage?: string;
    prompt?: string;
    replicateStrength?: 'low' | 'medium' | 'high';
    model?: string;
    size?: string;
    quality?: 'low' | 'medium' | 'high';
  }): Promise<{ data: EcommerceReplicateNode }> {
    const response = await platformFetch(`/ecommerce-replicate/tasks/${taskId}/nodes`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '创建节点失败');
    }
    return response.json();
  },

  // 更新节点
  async updateNode(id: string, data: {
    title?: string;
    referenceImage?: string | null;
    prompt?: string | null;
    replicateStrength?: 'low' | 'medium' | 'high';
    model?: string | null;
    size?: string;
    quality?: 'low' | 'medium' | 'high';
  }): Promise<{ data: EcommerceReplicateNode }> {
    const response = await platformFetch(`/ecommerce-replicate/nodes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '更新节点失败');
    }
    return response.json();
  },

  // 删除节点
  async deleteNode(id: string): Promise<{ message: string }> {
    const response = await platformFetch(`/ecommerce-replicate/nodes/${id}`, {
      method: 'DELETE',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '删除节点失败');
    }
    return response.json();
  },

  async getNodeCreditsPreview(id: string): Promise<{ data: EcommerceCreditsPreview }> {
    const response = await platformFetch(`/ecommerce-replicate/nodes/${id}/credits-preview`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '获取积分预览失败');
    }
    return response.json();
  },

  async getTaskCreditsPreview(taskId: string): Promise<{ data: EcommerceCreditsPreview }> {
    const response = await platformFetch(`/ecommerce-replicate/tasks/${taskId}/credits-preview`);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '获取积分预览失败');
    }
    return response.json();
  },

  // 生成单个节点
  async generateNode(id: string, params?: {
    model?: string;
    size?: string;
    quality?: string;
  }): Promise<{ message: string; nodeId: string; jobId: string; status: string }> {
    const response = await platformFetch(`/ecommerce-replicate/nodes/${id}/generate`, {
      method: 'POST',
      body: JSON.stringify(params || {}),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '生成失败');
    }
    return response.json();
  },

  // 一键生成全部
  async generateAll(taskId: string, params?: {
    model?: string;
    size?: string;
    quality?: string;
  }): Promise<{ message: string; taskId: string; nodeCount: number; results: Array<{ nodeId: string; jobId: string; status: string }> }> {
    const response = await platformFetch(`/ecommerce-replicate/tasks/${taskId}/generate-all`, {
      method: 'POST',
      body: JSON.stringify(params || {}),
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || '批量生成失败');
    }
    return response.json();
  },
};
